import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Grid, TextField,
  Chip, Tooltip, Alert, CircularProgress,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import PersonOff from '@mui/icons-material/PersonOff';
import PersonIcon from '@mui/icons-material/Person';
import { usersAPI } from '../services/api';

const ROLES = [
  { value: 'admin', label: 'Administrador', color: 'error' },
  { value: 'manager', label: 'Gestor', color: 'warning' },
  { value: 'engineer', label: 'Ingeniero', color: 'primary' },
  { value: 'viewer', label: 'Solo lectura', color: 'default' },
];

const CATEGORIES = [
  'Director de Proyectos', 'Ing. Senior', 'Ing. Junior', 'Tecnico Senior',
  'Tecnico', 'Delineante', 'Programador PLC', 'Administrativo', 'Comercial',
];

const DEPARTMENTS = [
  'Direccion', 'Ingenieria', 'Compras', 'Produccion', 'Comercial',
  'Administracion', 'Calidad', 'Mantenimiento',
];

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({});
  const [deleteConfirmUser, setDeleteConfirmUser] = useState(null);

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const res = await usersAPI.list();
      setUsers(res.data);
      setError(null);
    } catch (err) {
      setError('Error al cargar usuarios. Verifica permisos de administrador.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenNew = () => {
    setForm({ email: '', full_name: '', password: '', role: 'engineer', professional_category: '', department: '', phone: '' });
    setEditingUser(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (user) => {
    setForm({
      email: user.email || '',
      full_name: user.full_name || '',
      password: '',
      role: user.role || 'engineer',
      professional_category: user.professional_category || '',
      department: user.department || '',
      phone: user.phone || '',
      is_active: user.is_active,
    });
    setEditingUser(user);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const data = { ...form };
      if (!data.password) delete data.password;
      // Clean empty strings to null
      Object.keys(data).forEach(k => { if (data[k] === '') data[k] = null; });

      if (editingUser) {
        await usersAPI.update(editingUser.id, data);
      } else {
        if (!form.password) {
          setError('La contrasena es obligatoria para nuevos usuarios');
          return;
        }
        await usersAPI.create(form);
      }
      setDialogOpen(false);
      await loadUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar usuario');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await usersAPI.update(user.id, { is_active: !user.is_active });
      await loadUsers();
    } catch (err) {
      setError('Error al cambiar estado del usuario');
    }
  };

  const handleDelete = async (user) => {
    try {
      await usersAPI.delete(user.id);
      await loadUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const getRoleChip = (role) => {
    const r = ROLES.find(x => x.value === role) || ROLES[3];
    return <Chip label={r.label} color={r.color} size="small" />;
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" fontWeight="bold">Gestion de Usuarios</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleOpenNew}>Nuevo Usuario</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#c62828' }}>
              <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Nombre</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Rol</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Categoria</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Departamento</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Estado</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#fff' }} align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} hover sx={{ opacity: u.is_active ? 1 : 0.5 }}>
                <TableCell><strong>{u.full_name}</strong></TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>{getRoleChip(u.role)}</TableCell>
                <TableCell>{u.professional_category || '-'}</TableCell>
                <TableCell>{u.department || '-'}</TableCell>
                <TableCell>
                  <Chip label={u.is_active ? 'Activo' : 'Inactivo'} color={u.is_active ? 'success' : 'default'} size="small" />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => handleOpenEdit(u)}><Edit fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title={u.is_active ? 'Desactivar' : 'Activar'}>
                    <IconButton size="small" onClick={() => handleToggleActive(u)}>
                      {u.is_active ? <PersonOff fontSize="small" /> : <PersonIcon fontSize="small" color="success" />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Eliminar">
                    <IconButton size="small" color="error" onClick={() => setDeleteConfirmUser(u)}><Delete fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Nombre completo" value={form.full_name || ''} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Email" type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label={editingUser ? 'Nueva contrasena (dejar vacio para no cambiar)' : 'Contrasena'} type="password" value={form.password || ''} onChange={(e) => setForm({ ...form, password: e.target.value })} required={!editingUser} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth select label="Rol" value={form.role || 'engineer'} onChange={(e) => setForm({ ...form, role: e.target.value })} SelectProps={{ native: true }}>
                {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth select label="Categoria profesional" value={form.professional_category || ''} onChange={(e) => setForm({ ...form, professional_category: e.target.value })} SelectProps={{ native: true }}>
                <option value="">-- Seleccionar --</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth select label="Departamento" value={form.department || ''} onChange={(e) => setForm({ ...form, department: e.target.value })} SelectProps={{ native: true }}>
                <option value="">-- Seleccionar --</option>
                {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Telefono" value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>{editingUser ? 'Actualizar' : 'Crear Usuario'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmUser !== null} onClose={() => setDeleteConfirmUser(null)} maxWidth="xs">
        <DialogTitle>Confirmar eliminacion</DialogTitle>
        <DialogContent>
          <Typography>¿Estas seguro de que deseas eliminar al usuario "{deleteConfirmUser?.full_name}"?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmUser(null)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={async () => { await handleDelete(deleteConfirmUser); setDeleteConfirmUser(null); }}>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
